import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { api } from "../../App";
import { decryptResponse } from "../CryptoUtil";

const ENTRIES_PER_PAGE = 10;

const getStoredSession = () => {
  try {
    const stored = localStorage.getItem("userSession");

    if (!stored) return null;

    return typeof stored === "string"
      ? JSON.parse(stored)
      : stored;
  } catch (error) {
    console.error("Unable to read userSession:", error);
    return null;
  }
};

const normalize = (value) =>
  String(value ?? "").trim().toLowerCase();

const getSessionToken = (session) =>
  session?.token ??
  session?.accessToken ??
  session?.access_token ??
  "";

const decodeApiResponse = (responseData) => {
  if (!responseData?.encrypted) {
    return responseData;
  }

  const { payload, transactionId } = responseData.encrypted;

  if (!payload || !transactionId) {
    throw new Error(
      "Encrypted response is missing payload or transactionId."
    );
  }

  return decryptResponse(payload, transactionId);
};

const getWorkingArea = (zsm) => {
  const area = zsm?.working_area ?? zsm?.working_area_new;

  if (!area) return [];

  let parsed = area;

  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return parsed
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  if (Array.isArray(parsed)) {
    return parsed
      .map((item) => {
        if (typeof item === "string") return item;

        if (item && typeof item === "object") {
          return (
            item.state ??
            item.name ??
            item.label ??
            ""
          );
        }

        return "";
      })
      .filter(Boolean);
  }

  if (parsed && typeof parsed === "object") {
    // Supports: { "Madhya Pradesh": { "Bhopal": ["Huzur"] } }
    return Object.keys(parsed);
  }

  return [];
};

const getErrorMessage = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  try {
    const decoded = decodeApiResponse(data);

    if (decoded?.message) {
      return decoded.message;
    }
  } catch (decodeError) {
    console.error("Error response decryption failed:", decodeError);
  }

  if (status === 401) {
    return "Authentication failed. Please log out and log in again.";
  }

  if (status === 403) {
    return "You are not authorized to view ZSM records.";
  }

  if (status === 404) {
    return "getZsm.php endpoint was not found. Please check the API URL.";
  }

  if (status >= 500) {
    return "Server error while fetching ZSM records. Check the PHP error log.";
  }

  if (error?.code === "ECONNABORTED") {
    return "Request timed out. Please try again.";
  }

  return error?.message || "Unable to fetch ZSM records.";
};

const ZsmManagement = () => {
  const [zsmData, setZsmData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchZsmData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const session = getStoredSession();
      const token = getSessionToken(session);

      if (!token) {
        throw new Error(
          "Login token is missing from userSession. Please log out and log in again."
        );
      }

      const headers = {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      };

      console.log("Fetching ZSM records:", {
        endpoint: `${api}getZsm.php`,
        role: session?.user?.role ?? session?.role,
        tokenPresent: Boolean(token),
      });

      const response = await axios.get(`${api}getZsm.php`, {
        headers,
        withCredentials: true,
        timeout: 20000,
      });

      console.log("Raw ZSM response:", response.data);

      const result = decodeApiResponse(response.data);

      console.log("Decrypted ZSM response:", result);

      if (result?.success !== true) {
        throw new Error(
          result?.message || "The server could not fetch ZSM records."
        );
      }

      if (!Array.isArray(result.data)) {
        throw new Error(
          "Invalid API response: expected data to be an array."
        );
      }

      setZsmData(result.data);
      setCurrentPage(1);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("ZSM fetch failed:", err);
      console.error("HTTP status:", err?.response?.status);
      console.error("Server response:", err?.response?.data);

      setZsmData([]);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchZsmData();
  }, [fetchZsmData]);

  const filteredData = useMemo(() => {
    const search = normalize(searchTerm);

    if (!search) return zsmData;

    return zsmData.filter((zsm) => {
      const areas = getWorkingArea(zsm).join(" ");

      const searchableText = [
        zsm.id,
        zsm.name,
        zsm.email,
        zsm.phone,
        zsm.status,
        zsm.state,
        areas,
      ]
        .map(normalize)
        .join(" ");

      return searchableText.includes(search);
    });
  }, [zsmData, searchTerm]);

  const totalPages = Math.ceil(
    filteredData.length / ENTRIES_PER_PAGE
  );

  const safePage = Math.max(
    1,
    Math.min(currentPage, Math.max(totalPages, 1))
  );

  const startIndex = (safePage - 1) * ENTRIES_PER_PAGE;

  const currentEntries = filteredData.slice(
    startIndex,
    startIndex + ENTRIES_PER_PAGE
  );

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getStatusBadge = (status) => {
    const active = normalize(status) === "active";

    return (
      <span
        className={`badge ${
          active ? "bg-success" : "bg-secondary"
        }`}
      >
        {status || "Unknown"}
      </span>
    );
  };

  const handleEdit = (zsm) => {
    window.alert(
      `Selected ZSM: ${zsm.name || "Unnamed"} (${zsm.id}).\n\nTo save changes, connect this action to your ZSM update endpoint.`
    );
  };

  const handleStatus = (zsm) => {
    window.alert(
      `Current status: ${zsm.status || "Unknown"}.\n\nStatus changes require a backend update endpoint. No database changes have been made.`
    );
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">ZSM Management</h2>
          <p className="text-secondary mb-0">
            View and manage Zonal Sales Managers.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={fetchZsmData}
          disabled={loading}
        >
          <i
            className={`mdi ${
              loading
                ? "mdi-loading mdi-spin"
                : "mdi-refresh"
            } me-1`}
          />
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div
          className="alert alert-danger d-flex justify-content-between align-items-start gap-3"
          role="alert"
        >
          <div>
            <i className="mdi mdi-alert-circle-outline me-2" />
            <strong>Unable to load ZSM records</strong>
            <div className="mt-1">{error}</div>
          </div>

          <button
            type="button"
            className="btn btn-sm btn-outline-danger flex-shrink-0"
            onClick={fetchZsmData}
            disabled={loading}
          >
            Retry
          </button>
        </div>
      )}

      {/* Summary */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6 col-xl-4">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4 d-flex align-items-center gap-3">
              <div
                className="rounded-3 bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center"
                style={{ width: 52, height: 52 }}
              >
                <i className="mdi mdi-account-group-outline fs-3" />
              </div>

              <div>
                <div className="text-secondary small">
                  Total ZSM Records
                </div>
                <h3 className="fw-bold mb-0">
                  {loading ? "—" : zsmData.length}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-xl-4">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4 d-flex align-items-center gap-3">
              <div
                className="rounded-3 bg-success bg-opacity-10 text-success d-flex align-items-center justify-content-center"
                style={{ width: 52, height: 52 }}
              >
                <i className="mdi mdi-check-circle-outline fs-3" />
              </div>

              <div>
                <div className="text-secondary small">
                  Active ZSMs
                </div>
                <h3 className="fw-bold mb-0">
                  {loading
                    ? "—"
                    : zsmData.filter(
                        (zsm) =>
                          normalize(zsm.status) === "active"
                      ).length}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-xl-4">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4 d-flex align-items-center gap-3">
              <div
                className="rounded-3 bg-info bg-opacity-10 text-info d-flex align-items-center justify-content-center"
                style={{ width: 52, height: 52 }}
              >
                <i className="mdi mdi-filter-outline fs-3" />
              </div>

              <div>
                <div className="text-secondary small">
                  Matching Records
                </div>
                <h3 className="fw-bold mb-0">
                  {loading ? "—" : filteredData.length}
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="card border-0 shadow-sm rounded-4 mb-4">
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h5 className="fw-bold mb-1">Search ZSM</h5>
              <p className="text-secondary small mb-0">
                Search by ID, name, email, phone, status or working area.
              </p>
            </div>

            {lastUpdated && (
              <small className="text-secondary">
                Last refreshed: {lastUpdated.toLocaleTimeString()}
              </small>
            )}
          </div>

          <div className="input-group">
            <span className="input-group-text">
              <i className="mdi mdi-magnify" />
            </span>

            <input
              id="zsmSearch"
              type="search"
              className="form-control"
              placeholder="Search ZSM records..."
              value={searchTerm}
              onChange={handleSearch}
            />

            {searchTerm && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={clearSearch}
              >
                <i className="mdi mdi-close me-1" />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
            <div>
              <h5 className="fw-bold mb-1">ZSM List</h5>
              <p className="text-secondary small mb-0">
                Review ZSM details and assigned working areas.
              </p>
            </div>

            <span className="badge bg-light text-dark border">
              {filteredData.length} entries
            </span>
          </div>

          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="px-4">S.No.</th>
                  <th>ZSM ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Working Area</th>
                  <th>Status</th>
                  <th className="text-center px-4">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-5"
                    >
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Loading ZSM records...
                    </td>
                  </tr>
                ) : currentEntries.length > 0 ? (
                  currentEntries.map((zsm, index) => {
                    const areas = getWorkingArea(zsm);

                    return (
                      <tr key={zsm.id || `${zsm.name}-${index}`}>
                        <td className="px-4">
                          {startIndex + index + 1}
                        </td>

                        <td>
                          <span className="fw-semibold">
                            {zsm.id || "—"}
                          </span>
                        </td>

                        <td>
                          <span className="fw-semibold">
                            {zsm.name || "—"}
                          </span>
                        </td>

                        <td>{zsm.email || "—"}</td>
                        <td>{zsm.phone || "—"}</td>

                        <td>
                          {areas.length ? (
                            <div className="d-flex flex-wrap gap-1">
                              {areas.map((area, areaIndex) => (
                                <span
                                  key={`${area}-${areaIndex}`}
                                  className="badge bg-light text-dark border"
                                >
                                  {area}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-secondary">—</span>
                          )}
                        </td>

                        <td>{getStatusBadge(zsm.status)}</td>

                        <td className="px-4">
                          <div className="d-flex justify-content-center align-items-center gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleEdit(zsm)}
                              title="Edit ZSM"
                            >
                              <i className="mdi mdi-pencil-outline me-1" />
                              Edit
                            </button>

                            <button
                              type="button"
                              className={`btn btn-sm ${
                                normalize(zsm.status) === "active"
                                  ? "btn-outline-danger"
                                  : "btn-outline-success"
                              }`}
                              onClick={() => handleStatus(zsm)}
                              title="Change ZSM status"
                            >
                              {normalize(zsm.status) === "active"
                                ? "Inactive"
                                : "Active"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-5"
                    >
                      <i className="mdi mdi-account-search-outline fs-1 text-secondary" />

                      <h6 className="fw-semibold mt-2">
                        {error
                          ? "Records could not be loaded"
                          : searchTerm
                            ? "No matching records found"
                            : "No ZSM records found"}
                      </h6>

                      <p className="text-secondary mb-3">
                        {error
                          ? "Resolve the error above and try again."
                          : searchTerm
                            ? "Try another search term."
                            : "There are no ZSM records available for this account."}
                      </p>

                      {!error && searchTerm && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={clearSearch}
                        >
                          Clear Search
                        </button>
                      )}

                      {error && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={fetchZsmData}
                          disabled={loading}
                        >
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && filteredData.length > 0 && (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 p-4 border-top">
              <small className="text-secondary">
                Showing {startIndex + 1}–
                {Math.min(
                  startIndex + ENTRIES_PER_PAGE,
                  filteredData.length
                )}{" "}
                of {filteredData.length} entries
              </small>

              <nav aria-label="ZSM pagination">
                <ul className="pagination pagination-sm mb-0">
                  <li
                    className={`page-item ${
                      safePage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="page-link"
                      disabled={safePage === 1}
                      onClick={() => handlePageChange(safePage - 1)}
                      aria-label="Previous page"
                    >
                      &laquo;
                    </button>
                  </li>

                  {Array.from(
                    { length: totalPages },
                    (_, index) => index + 1
                  ).map((page) => (
                    <li
                      key={page}
                      className={`page-item ${
                        safePage === page ? "active" : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                  <li
                    className={`page-item ${
                      safePage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <button
                      type="button"
                      className="page-link"
                      disabled={safePage === totalPages}
                      onClick={() => handlePageChange(safePage + 1)}
                      aria-label="Next page"
                    >
                      &raquo;
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ZsmManagement;

